import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Monitor, Smartphone, ShoppingCart, TrendingUp } from 'lucide-react';
import { NavLink, Link } from 'react-router-dom';
import logo from '../assets/logo.png';

// Add custom styles for animations and responsive design
const customStyles = `
  @keyframes slide-in {
    from {
      opacity: 0;
      transform: translateX(10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  .animate-slide-in {
    animation: slide-in 0.3s ease-out forwards;
  }
  
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
  
  @keyframes pulse-glow {
    0%, 100% {
      box-shadow: 0 0 20px rgba(34, 197, 94, 0.4);
    }
    50% {
      box-shadow: 0 0 30px rgba(34, 197, 94, 0.6), 0 0 40px rgba(34, 197, 94, 0.3);
    }
  }
  
  @keyframes slide-text {
    0% {
      transform: translateX(100%);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.3s ease-out forwards;
  }
  
  .btn-shimmer {
    position: relative;
    overflow: hidden;
  }
  
  .btn-shimmer::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: shimmer 2s infinite;
  }
  
  .btn-glow:hover {
    animation: pulse-glow 2s infinite;
  }
  
  .text-slide {
    animation: slide-text 0.6s ease-out;
  }
  
  .gradient-border {
    position: relative;
    background: linear-gradient(45deg, #10b981, #059669);
    padding: 2px;
    border-radius: 9999px;
  }
  
  .gradient-border::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 2px;
    background: linear-gradient(45deg, #10b981, #059669, #10b981);
    border-radius: inherit;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
  }

  .service-grid-item {
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .service-grid-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.1), transparent);
    transition: left 0.5s;
  }

  .service-grid-item:hover::before {
    left: 100%;
  }

  .service-grid-item:hover {
    background-color: rgba(16, 185, 129, 0.05);
    border-color: rgba(16, 185, 129, 0.2);
    transform: translateY(-2px);
  }

  .service-grid-item:hover .service-icon {
    transform: scale(1.1);
    color: #10b981;
  }

  .service-grid-item:hover .service-title {
    color: #10b981;
  }

  /* Responsive adjustments */
  @media (max-width: 640px) {
    .logo {
      height: 2rem !important;
    }
    
    .mobile-services {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease-out;
    }
    
    .mobile-services.open {
      max-height: 400px;
    }
    
    .services-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem 0;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
}

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrollY, setScrollY] = useState(0);
    const [isCategoryOpen, setIsCategoryOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Updated menu items with Contact as the last item
    const menuItems = [
        { name: 'Home', path: '/home' },
        { name: 'About', path: '/about' },
        { name: 'Products', path: '/products' },
    ];

    // Service items renamed to Category for clarity
    const categoryItems = [
        {
            name: 'Web Development',
            path: '/web-development',
            icon: Monitor,
        },
        {
            name: 'Mobile Apps',
            path: '/mobile-apps',
            icon: Smartphone,
        },
        {
            name: 'E-Commerce',
            path: '/ecommerce',
            icon: ShoppingCart,
        },
        {
            name: 'Digital Marketing',
            path: '/digital-marketing',
            icon: TrendingUp,
        },
    ];

    const handleCategoryLinkClick = () => {
        setIsCategoryOpen(false);
        setIsMenuOpen(false);
        window.scrollTo(0, 0);
    };

    return (
        <nav
            className={`fixed w-full z-50 transition-all duration-500 ${scrollY > 50
                ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100'
                : 'bg-white/80 backdrop-blur-sm'
                }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center py-4">
                    <div className="flex items-center space-x-3">
                        <Link to="/home">
                            <img src={logo} alt="OneHexa Tech" className="w-auto h-10 logo" />
                        </Link>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center space-x-8">
                        {menuItems.map((item) => (
                            <NavLink
                                key={item.name}
                                to={item.path}
                                onClick={() => window.scrollTo(0, 0)}
                                className={({ isActive }) =>
                                    `font-medium transition-all duration-300 hover:scale-105 transform relative ${isActive ? 'text-green-500 border-b-2 border-green-500' : 'text-gray-700 hover:text-green-500'}`
                                }
                            >
                                {item.name}
                            </NavLink>
                        ))}

                        {/* Category Dropdown with Grid */}
                        <div
                            className="relative group"
                            onMouseEnter={() => setIsCategoryOpen(true)}
                            onMouseLeave={() => setIsCategoryOpen(false)}
                        >
                            <button
                                className="flex items-center gap-1 font-medium text-gray-700 hover:text-green-500 transition-all duration-300"
                            >
                                Category <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isCategoryOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Invisible bridge to prevent dropdown from closing */}
                            <div className="absolute top-full left-0 w-full h-2 bg-transparent"></div>

                            {/* Grid Dropdown */}
                            <div className={`absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 w-[520px] z-50 transition-all duration-300 origin-top-right ${isCategoryOpen
                                ? 'opacity-100 scale-100 translate-y-0'
                                : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                                }`}>

                                <div className="mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-1">Our Categories</h3>
                                    <p className="text-sm text-gray-500">Comprehensive solutions for your business needs</p>
                                </div>

                                {/* 2x4 Grid Layout */}
                                <div className="grid grid-cols-2 gap-3">
                                    {categoryItems.map((category, index) => {
                                        const IconComponent = category.icon;
                                        return (
                                            <Link
                                                key={category.name}
                                                to={category.path}
                                                onClick={handleCategoryLinkClick}
                                                className={`service-grid-item flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-green-200 transition-all duration-300 group ${isCategoryOpen ? "animate-fade-in-up" : ""
                                                    }`}
                                                style={{
                                                    animationDelay: `${index * 50}ms`,
                                                    animationFillMode: "both",
                                                }}
                                            >
                                                <div className="flex-shrink-0">
                                                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                                                        <IconComponent className="service-icon w-5 h-5 text-green-600 transition-all duration-300" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="service-title text-sm font-semibold text-gray-800 transition-colors duration-300 group-hover:text-gray-600">
                                                        {category.name}
                                                    </h4>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>

                                {/* Footer */}
                                <div className="mt-6 pt-4 border-t border-gray-100">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs text-gray-400">Need custom solutions?</p>
                                        <Link to="/contact"
                                            className="text-xs text-green-600 hover:text-green-700 font-medium transition-colors duration-200"
                                            onClick={handleCategoryLinkClick}
                                        >
                                            Contact Us →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact as the last item */}
                        <NavLink
                            to="/contact"
                            onClick={() => window.scrollTo(0, 0)}
                            className={({ isActive }) =>
                                `font-medium transition-all duration-300 hover:scale-105 transform relative ${isActive ? 'text-green-500 border-b-2 border-green-500' : 'text-gray-700 hover:text-green-500'}`
                            }
                        >
                            Contact
                        </NavLink>

                        <Link to="/contact">
                            <div className="gradient-border">
                                <button className="relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-3 rounded-full font-medium transition-all duration-500 transform hover:scale-110 btn-shimmer btn-glow group overflow-hidden">
                                    <span className="relative z-10 flex items-center gap-2 text-slide">
                                        <span className="transition-transform duration-300 group-hover:translate-x-1">
                                            Get Started
                                        </span>
                                        <svg
                                            className="w-4 h-4 transition-all duration-300 transform group-hover:translate-x-2 group-hover:scale-110"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                    </span>

                                    <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full"></div>

                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                        <div className="absolute top-1/2 left-2 w-1 h-1 bg-white rounded-full animate-ping" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="absolute top-1/4 left-1/4 w-0.5 h-0.5 bg-white/70 rounded-full animate-ping" style={{ animationDelay: '0.3s' }}></div>
                                        <div className="absolute bottom-1/4 left-1/2 w-0.5 h-0.5 bg-white/50 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                                    </div>
                                </button>
                            </div>
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden text-gray-700 hover:text-green-500 transition-colors p-2 rounded-lg hover:bg-gray-100"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                        {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <div
                className={`md:hidden transition-all duration-300 ${isMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                    } overflow-hidden bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-lg`}
            >
                <div className="px-4 py-4 space-y-2">
                    {menuItems.map((item) => (
                        <NavLink
                            key={item.name}
                            to={item.path}
                            className={({ isActive }) =>
                                `block font-medium transition-colors py-2 px-2 border-b border-gray-100 last:border-b-0 ${isActive ? 'text-green-500 border-b-2 border-green-500' : 'text-gray-700 hover:text-green-500'}`
                            }
                            onClick={() => setIsMenuOpen(false)}
                        >
                            {item.name}
                        </NavLink>
                    ))}

                    {/* Mobile Category Grid */}
                    <div>
                        <button
                            className="services-toggle w-full font-medium text-gray-700 hover:text-green-500 transition-colors py-2 px-2 border-b border-gray-100 ml-2"
                            onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                        >
                            Category
                            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isCategoryOpen ? 'rotate-180' : ''}`} />
                        </button>

                        <div className={`mobile-services ${isCategoryOpen ? 'open' : ''} px-2 py-2`}>
                            <div className="grid grid-cols-1 gap-2">
                                {categoryItems.map((category) => {
                                    const IconComponent = category.icon;
                                    return (
                                        <Link
                                            key={category.name}
                                            to={category.path}
                                            className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-green-200 hover:bg-green-50 transition-all duration-200"
                                            onClick={handleCategoryLinkClick}
                                        >
                                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <IconComponent className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="text-sm font-medium text-gray-800">{category.name}</h4>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Contact as the last item in mobile view */}
                    <NavLink
                        to="/contact"
                        className={({ isActive }) =>
                            `block font-medium transition-colors py-2 px-2 border-b border-gray-100 last:border-b-0 ${isActive ? 'text-green-500 border-b-2 border-green-500' : 'text-gray-700 hover:text-green-500'}`
                        }
                        onClick={() => setIsMenuOpen(false)}
                    >
                        Contact
                    </NavLink>

                    <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                        <button className="w-full relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-2 rounded-full font-medium mt-4 btn-shimmer group overflow-hidden transition-all duration-300 transform hover:scale-105">
                            <span className="relative z-10 flex items-center justify-center gap-2">
                                <span className="transition-transform duration-300 group-hover:translate-x-1">
                                    Get Started
                                </span>
                                <svg
                                    className="w-4 h-4 transition-all duration-300 transform group-hover:translate-x-1 group-hover:scale-110"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>
                        </button>
                    </Link>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;